import React, { createRef, useState } from 'react';
import { Avatar, Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Clear as ClearIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { UploadArea, hovered } from './styled';
import { grey } from '@mui/material/colors';
import { GeneralLayout } from '../../../../layouts';
import { useNavigate, useParams } from 'react-router';
import { maxCasePdfReportSizeInMegabytes } from '../../../../utils/constants';
import { useMutation } from '@apollo/client';
import { updateCaseMutation } from './mutations';
import { webhookClient } from '../../../../utils/webhookClient';
import { LoadingButton } from '@mui/lab';

const AddResults = (): React.ReactElement => {
  const { case_id } = useParams();
  const navigate = useNavigate();

  const [isDragged, setIsDragged] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [resultsText, setResultsText] = useState<string>('');
  const [uploadLoading, setUploadLoading] = useState(false);

  const inputRef = createRef<HTMLInputElement>();
  const inputSettings = {
    multiple: false,
    disabled: false,
    accept: {
      'application/pdf': ['.pdf'],
    },
  };

  const onDrop = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
  };

  const { getInputProps, getRootProps } = useDropzone({
    ...inputSettings,
    onDrop,
    onDragLeave: () => setIsDragged(false),
    onDragOver: () => setIsDragged(true),
    noClick: false,
    maxSize: maxCasePdfReportSizeInMegabytes * 1024 ** 2,
  });

  const handleDelete = () => {
    setFile(null);
  };

  const handleSave = async () => {
    if (resultsText) {
      await runMutation();
    }

    if (file) {
      await uploadFile();
    }

    navigate(`/lab/cases/${case_id}`);
  };

  const uploadFile = async () => {
    const formData = new FormData();
    setUploadLoading(true);
    formData.append('file', file!);
    await webhookClient.postFormData(`cases/${case_id}/upload-report`, formData);
    setUploadLoading(false);
  };

  const [runMutation, { loading: updateLoading }] = useMutation(updateCaseMutation, {
    variables: {
      pk_columns: { id: case_id },
      caseId: case_id,
      set: {
        reports_text: resultsText,
      },
      status: 'results_added',
    },
  });

  const loading = updateLoading || uploadLoading;

  return (
    <GeneralLayout
      breadcrumbs={[{ label: 'Cases', link: `/lab/cases` }, { label: case_id || '', link: `/lab/cases/${case_id}` }, { label: 'Add Results' }]}
      currentPage="Add Results">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField value={resultsText} onChange={(e) => setResultsText(e.target.value)} minRows={3} maxRows={5} multiline fullWidth label="Results"></TextField>
        </Grid>
        <Grid item xs={12}>
          <UploadArea sx={(theme) => (isDragged ? { ...hovered(theme) } : {})} {...getRootProps()} mb={4}>
            <input {...getInputProps()} />
            <Box mb={1} width="100%" display="flex" justifyContent="center">
              <Avatar sx={(theme) => ({ bgcolor: `${theme.palette.primary.main}40`, width: 32, height: 32 })}>
                <UploadFileIcon fontSize="small" sx={(theme) => ({ fill: theme.palette.primary.main })} />
              </Avatar>
            </Box>
            <Box mb={1}>
              <Typography
                onClick={() => inputRef.current?.click()}
                variant="body2"
                component="span"
                sx={(theme) => ({ color: theme.palette.primary.main, borderBottom: `1px solid ${theme.palette.primary.main}` })}>
                Click to upload
              </Typography>{' '}
              <Typography variant="body2" component="span">
                or drag and drop PDF file
              </Typography>
            </Box>
            <Box>
              <Typography color={grey[600]} variant="body2">
                (max. {maxCasePdfReportSizeInMegabytes} MB)
              </Typography>
            </Box>
          </UploadArea>
        </Grid>
        <Grid item xs={12}>
          {file && (
            <Box mb={2} display={'flex'}>
              <Box mr={2}>
                <Avatar sx={(theme) => ({ bgcolor: `${theme.palette.primary.main}40`, width: 32, height: 32 })}>
                  <UploadFileIcon fontSize="small" sx={(theme) => ({ fill: theme.palette.primary.main })} />
                </Avatar>
              </Box>
              <Box flexGrow={1}>
                <Typography component={'span'} variant="body2">
                  {file.name}
                </Typography>
              </Box>
              <Box textAlign={'end'}>
                <IconButton onClick={() => handleDelete()}>
                  <ClearIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <LoadingButton loading={loading} onClick={handleSave} disabled={!resultsText && !file} variant="contained" fullWidth>
          Add Results
        </LoadingButton>
      </Grid>
    </GeneralLayout>
  );
};

export default AddResults;
